import { useContext, useMemo } from "react";

import { IconBadge } from "@/DesignSystem/Molecules/IconBadge";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { CareElementGridHelper } from "@/Flowsheets/Helpers/CareElementGridHelper";
import { CareElementOperatorType } from "@/Flowsheets/Models/CareElementOperatorType";
import { AgentDoseReviewedCareElement } from "@/Flowsheets/Models/CareElements/AgentDoseReviewedCareElement";
import { BooleanReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanReviewedCareElement";
import { BooleanTextReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanTextReviewedCareElement";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { DateOnlyReviewedCareElement } from "@/Flowsheets/Models/CareElements/DateOnlyReviewedCareElement";
import { DecimalReviewedCareElement } from "@/Flowsheets/Models/CareElements/DecimalReviewedCareElement";
import { FirstLineReviewedCareElement } from "@/Flowsheets/Models/CareElements/FirstLineReviewedCareElement";
import { IntegerReviewedCareElement } from "@/Flowsheets/Models/CareElements/IntegerReviewedCareElement";
import { MultiSelectReviewedCareElement } from "@/Flowsheets/Models/CareElements/MultiSelectReviewedCareElement";
import { TextReviewedCareElement } from "@/Flowsheets/Models/CareElements/TextReviewedCareElement";
import { CareElementDateReviewed } from "./CareElementDateReviewed";
import { CareElementTarget } from "./CareElementTarget";
import { CareElementWithFrequencyLabel } from "./CareElementWithFrequencyLabel";

import styles from "./CareElementDateReviewedRow.module.scss";

type CareElementsWithDateReviewed =
    | BooleanReviewedCareElement
    | BooleanTextReviewedCareElement
    | TextReviewedCareElement
    | IntegerReviewedCareElement
    | DecimalReviewedCareElement
    | MultiSelectReviewedCareElement
    | DateOnlyReviewedCareElement
    | AgentDoseReviewedCareElement
    | FirstLineReviewedCareElement;

interface ICareElementDateReviewedProps {
    element: CareElementsWithDateReviewed;
    onChange: (element: CareElementsWithDateReviewed) => void;
    isEditable: boolean;
    label: string | JSX.Element;
    side: "row" | "column";
    children?: React.ReactNode;
    prefix?: React.ReactNode;
    isInputControl?: boolean;
    setFalseAsValid?: boolean;
    isDatePickerVisible?: boolean;
}

/**
 * Determines whether to show an alarming icon based on the comparison of values.
 * @param value The current value to compare.
 * @param target The target value to compare against.
 * @param operator The operator to use for comparison.
 * @returns True if the alarming icon should be shown, false otherwise.
 */
function showAlarmingIcon(
    value: boolean | string | number | Date | null,
    target: boolean | string | null | Date | number,
    operator: CareElementOperatorType
): boolean {
    if (!operator || target === null || value === null) {
        return false;
    }

    if (target !== null && value === null) {
        return true;
    }

    if (
        (typeof value === "boolean" && typeof target === "boolean") ||
        (typeof target === "string" && typeof value === "string")
    ) {
        return operator === CareElementOperatorType.EqualTo && value !== target;
    }

    if (
        typeof value === "number" &&
        typeof target === "number" &&
        !isNaN(value)
    ) {
        return CareElementGridHelper.isOutOfRange(value, target, operator);
    }

    return false;
}

function CareElementDateReviewedRow(
    props: Readonly<ICareElementDateReviewedProps>
): JSX.Element {
    const {
        element,
        onChange,
        isEditable,
        label,
        side,
        children,
        isInputControl = false,
        setFalseAsValid,
        isDatePickerVisible
    } = props;

    const { encounterDate } = useContext(EncounterDateContext);

    const showIcon = useMemo(() => {
        if (element instanceof MultiSelectReviewedCareElement) {
            return (
                element.Value?.any() &&
                element.Value.every(value =>
                    showAlarmingIcon(value, element.Target, element.Operator)
                )
            );
        }

        if (element.CareElementType === CareElementType.AgentDoseReviewed ||
            element.CareElementType === CareElementType.FirstLineReviewed
        ) {
            return false;
        }

        return showAlarmingIcon(
            element.Value,
            element.Target,
            element.Operator
        );
    }, [element]);

    return (
        <>
            <div
                className={`${styles.careElementValueContainer} ${styles[side]}`}
            >
                {props.prefix && (
                    <div className={styles.prefixContainer}>{props.prefix}</div>
                )}
                <div className={`${styles.nameContainer} ${side === "row" ? styles.nameContainerRow : ""}`}>
                    <CareElementWithFrequencyLabel
                        label={label}
                        keyName={`${element.Name}-frequency`}
                        state={element}
                        encounterDate={encounterDate}
                    />
                </div>
                <div className={styles.optionContainer}>
                    <div className={styles.valueContainer}>
                        {children}
                        {element.CareElementType !== CareElementType.FirstLineReviewed && element.Operator > 0 && (
                            <CareElementTarget
                                value={element.Target}
                                operator={element.Operator}
                            />
                        )}
                    </div>
                    {showIcon && (
                        <span
                            className={isInputControl
                                ? styles.alarmingIconNumberInput
                                : styles.alarmingIcon
                            }
                        >
                            <IconBadge variant="alarming" size="sm" />
                        </span>
                    )}
                </div>
            </div>
            <CareElementDateReviewed
                id={`care-element-date-reviewed-${element?.Name}`}
                isEditable={isEditable}
                dataCy={`care-element-date-reviewed-${element?.Name}`}
                encounterDate={encounterDate}
                state={element}
                handleChange={date =>
                    onChange({ ...element, DateReviewedWithPrecision: date })
                }
                setFalseAsValid={setFalseAsValid}
                isDatePickerVisible={isDatePickerVisible}
            />
        </>
    );
}
export { CareElementDateReviewedRow, showAlarmingIcon };
